/**
 * Hook for sandbox monitoring - manages a single exec stream
 * that collects both stats and process information.
 *
 * @module components/terminal/useSandboxMonitor
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { useExecStream, type ConnectionState } from '@/hooks';

export interface CpuStats {
  user: number;
  nice: number;
  system: number;
  idle: number;
  iowait: number;
  irq: number;
  softirq: number;
  steal: number;
}

export interface MemoryStats {
  total_kb: number;
  free_kb: number;
  available_kb: number;
}

export interface LoadAvg {
  '1min': number;
  '5min': number;
  '15min': number;
}

export interface NetworkStats {
  rx_bytes: number;
  tx_bytes: number;
}

export interface DiskEntry {
  /** Mount path. Deprecated: use label instead. */
  mount?: string;
  size: string;
  used: string;
  avail: string;
  percent: string;
  /** Human-friendly label (e.g., "container-0"). If set, UI should prefer this over mount. */
  label?: string;
}

export interface StatsData {
  cpu: CpuStats;
  memory: MemoryStats;
  loadavg: LoadAvg;
  uptime_secs: number;
  network: NetworkStats;
  disk: DiskEntry[];
}

export interface SandboxMonitorData {
  connectionState: ConnectionState;
  stats: StatsData | null;
  prevCpu: CpuStats | null;
  networkRate: { rx: number; tx: number };
  error: string | null;
  retry: () => void;
}

// Script that collects stats JSON
const MONITOR_SCRIPT = `
collect_stats() {
  cpu_line=$(head -1 /proc/stat | cut -d' ' -f2-)
  set -- $cpu_line
  user=$1; nice=$2; system=$3; idle=$4; iowait=$5; irq=$6; softirq=$7; steal=$8
  mem_total=$(awk '/MemTotal/ {print $2}' /proc/meminfo)
  mem_free=$(awk '/MemFree/ {print $2}' /proc/meminfo)
  mem_available=$(awk '/MemAvailable/ {print $2}' /proc/meminfo)
  read la1 la5 la15 rest < /proc/loadavg
  uptime_secs=$(awk '{print int($1)}' /proc/uptime)
  rx_bytes=$(awk 'NR>2 && $1!="lo:" {sum+=$2} END{print sum+0}' /proc/net/dev)
  tx_bytes=$(awk 'NR>2 && $1!="lo:" {sum+=$10} END{print sum+0}' /proc/net/dev)
  disk_json=$(df -h 2>/dev/null | awk 'NR>1 && $1!="tmpfs" && $1!="devtmpfs" {
    gsub(/%/,"",$5);
    printf "%s{\\"mount\\":\\"%s\\",\\"size\\":\\"%s\\",\\"used\\":\\"%s\\",\\"avail\\":\\"%s\\",\\"percent\\":\\"%s\\"}",
      (NR==2?"":","), $6, $2, $3, $4, $5
  } END{if(NR<2) print ""}' | sed 's/^/[/;s/$/]/')
  echo "---STATS_JSON---"
  echo "{\\"cpu\\":{\\"user\\":$user,\\"nice\\":$nice,\\"system\\":$system,\\"idle\\":$idle,\\"iowait\\":$iowait,\\"irq\\":$irq,\\"softirq\\":$softirq,\\"steal\\":$steal},\\"memory\\":{\\"total_kb\\":$mem_total,\\"free_kb\\":$mem_free,\\"available_kb\\":$mem_available},\\"loadavg\\":{\\"1min\\":$la1,\\"5min\\":$la5,\\"15min\\":$la15},\\"uptime_secs\\":$uptime_secs,\\"network\\":{\\"rx_bytes\\":$rx_bytes,\\"tx_bytes\\":$tx_bytes},\\"disk\\":$disk_json}"
  echo "---STATS_END---"
}
export TERM=xterm-256color
while true; do
  collect_stats 2>/dev/null
  sleep 2
done
`;

export function useSandboxMonitor(
  sandboxId: string,
  enabled: boolean = true,
  sandboxGroupId?: string
): SandboxMonitorData {
  const [connectionState, setConnectionState] = useState<ConnectionState>('disconnected');
  const [stats, setStats] = useState<StatsData | null>(null);
  const [prevCpu, setPrevCpu] = useState<CpuStats | null>(null);
  const [networkRate, setNetworkRate] = useState({ rx: 0, tx: 0 });
  const [error, setError] = useState<string | null>(null);
  const [commandSent, setCommandSent] = useState(false);
  const [retryCount, setRetryCount] = useState(0);

  const outputBufferRef = useRef<string>('');
  const lastUpdateRef = useRef<number>(Date.now());
  const prevNetworkRef = useRef<NetworkStats | null>(null);
  const prevCpuRef = useRef<CpuStats | null>(null);
  const disconnectRef = useRef<(() => void) | null>(null);
  const connectRef = useRef<((height?: number, width?: number) => void) | null>(null);
  const hasConnectedRef = useRef(false);

  const handleOutput = useCallback(
    (data: string) => {
      outputBufferRef.current += data;

      if (outputBufferRef.current.length > 100000) {
        outputBufferRef.current = outputBufferRef.current.slice(-50000);
      }

      const buffer = outputBufferRef.current;

      // Parse stats
      const statsStartIdx = buffer.indexOf('---STATS_JSON---');
      const statsEndIdx = buffer.indexOf('---STATS_END---');

      if (statsStartIdx !== -1 && statsEndIdx !== -1 && statsEndIdx > statsStartIdx) {
        const jsonStr = buffer.substring(statsStartIdx + 16, statsEndIdx).trim();
        try {
          const parsed = JSON.parse(jsonStr) as StatsData;

          const now = Date.now();
          const elapsed = (now - lastUpdateRef.current) / 1000;
          lastUpdateRef.current = now;

          // Calculate network rate using refs to avoid stale closures
          if (prevNetworkRef.current && elapsed > 0) {
            setNetworkRate({
              rx: (parsed.network.rx_bytes - prevNetworkRef.current.rx_bytes) / elapsed,
              tx: (parsed.network.tx_bytes - prevNetworkRef.current.tx_bytes) / elapsed,
            });
          }

          // Update prevCpu with current cpu before updating stats
          setPrevCpu(prevCpuRef.current);
          prevCpuRef.current = parsed.cpu;
          prevNetworkRef.current = parsed.network;
          setStats(parsed);
          setError(null);
        } catch {
          // JSON parse error
        }
      }

      // Clear buffer after stats are parsed
      if (statsEndIdx !== -1) {
        outputBufferRef.current = buffer.substring(statsEndIdx + 15);
      }
    },
    [] // No dependencies - using refs for mutable state
  );

  const handleExit = useCallback((exitCode: number) => {
    if (exitCode !== 0) {
      setError('Monitor exited unexpectedly');
    }
  }, []);

  const handleError = useCallback((err: string) => {
    setError(`Connection error: ${err}`);
  }, []);

  const handleStateChange = useCallback((state: ConnectionState) => {
    setConnectionState(state);
    if (state === 'connected') {
      setError(null);
    }
  }, []);

  const execStream = useExecStream({
    sandboxId,
    sandboxGroupId,
    command: '/bin/bash',
    environment: { HISTFILE: '/dev/null' },
    onOutput: handleOutput,
    onExit: handleExit,
    onError: handleError,
    onStateChange: handleStateChange,
  });

  // Keep refs updated
  useEffect(() => {
    disconnectRef.current = execStream.disconnect;
    connectRef.current = execStream.connect;
  }, [execStream.disconnect, execStream.connect]);

  // Send monitor script when connected
  useEffect(() => {
    if (connectionState === 'connected' && !commandSent) {
      const timer = setTimeout(() => {
        execStream.sendInput(MONITOR_SCRIPT + '\n');
        setCommandSent(true);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [connectionState, commandSent, execStream]);

  // Reset on retry
  useEffect(() => {
    setCommandSent(false);
    outputBufferRef.current = '';
  }, [retryCount]);

  // Auto-connect on mount (only when enabled)
  useEffect(() => {
    if (!enabled) return;
    const timer = setTimeout(() => {
      if (!hasConnectedRef.current && connectRef.current) {
        hasConnectedRef.current = true;
        connectRef.current(24, 120);
      }
    }, 100);

    return () => {
      clearTimeout(timer);
      if (disconnectRef.current) {
        disconnectRef.current();
      }
    };
  }, [enabled]);

  const retry = useCallback(() => {
    setRetryCount((c) => c + 1);
    setError(null);
    hasConnectedRef.current = false;
    execStream.disconnect();
    setTimeout(() => {
      hasConnectedRef.current = true;
      execStream.connect(24, 120);
    }, 100);
  }, [execStream]);

  return {
    connectionState,
    stats,
    prevCpu,
    networkRate,
    error,
    retry,
  };
}
